import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { NgxAuthService } from '@go-modules/services/auth/auth.service';
import { emailValidator } from '@go-modules/form-validator/email.validator/email.validator';
import { ResetPasswordSources } from '@go-modules/enums/';
import { BehaviorSubject, EMPTY, Subject, catchError, combineLatest, finalize, map, takeUntil } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';

@Component({
	selector: 'ngx-forgot-password',
	templateUrl: './forgot-password.component.html',
	styleUrls: ['./forgot-password.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ForgotPasswordComponent  implements OnInit, OnDestroy {

	@Input() public sessionId: string;
	@Input() public routeSource: ResetPasswordSources = ResetPasswordSources.DASHBOARD;
	@Output() public onBack: EventEmitter<void> = new EventEmitter();

	public form: UntypedFormGroup;
	public requestSuccess$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public requestError$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public isResetLocked$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public resetLockDuration$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
	public componentDestroyed$$ = new Subject();
	public disableButton$ = combineLatest([
		this.loading$,
		this.isResetLocked$,
		this.requestError$
	]).pipe(
		takeUntil(this.componentDestroyed$$),
		map(([loading, isResetLocked, requestError]) =>
			loading || isResetLocked || requestError
		)
	);
	private lockIntervalId;

	constructor (
		private authService: NgxAuthService,
		private translate: TranslateService
	) {}

	public ngOnInit () {
		this.form = new UntypedFormGroup({
			email: new UntypedFormControl(null, [Validators.required, emailValidator()])
		});

		this.form.get('email').valueChanges
			.pipe(takeUntil(this.componentDestroyed$$))
			.subscribe(() => this.requestError$.next(false));
	}

	public ngOnDestroy (): void {
		clearInterval(this.lockIntervalId);
		this.componentDestroyed$$.next(true);
		this.componentDestroyed$$.complete();
	}

	public submit (form) {
		if (form.invalid) {
			return;
		}

		this.loading$.next(true);
		this.authService.requestPassword(form.get('email').value, this.sessionId, this.routeSource)
			.pipe(
				catchError((response: HttpErrorResponse) => {
					if (response.status === 423) {
						this.isResetLocked$.next(true);
						let timeLeft = response.error.remainingResetLockSeconds;

						this.lockIntervalId = setInterval(() => {
							timeLeft -= 1;
							this.resetLockDuration$.next(timeLeft);

							if (timeLeft <= 0) {
								clearInterval(this.lockIntervalId);
								this.isResetLocked$.next(false);
							}
						}, 1000);
					} else {
						this.requestError$.next(true);
					}
					return EMPTY;
				}),
				finalize(() => {
					this.loading$.next(false);
				})
			)
			.subscribe(() => {
				this.requestSuccess$.next(true);
			});
	}

	public navigateBack () {
		this.onBack.emit();
	}

	public disableRequestPasswordButton () {
		return this.form.controls.email.hasError('required') || this.form.controls.email.hasError('email');
	}

	public getResendDurationMessage (duration) {
		return this.translate.instant('forgot-password-request-resend_in-message', {
			duration
		});
	}
}
